// ─── RSVP Page Editor ─────────────────────────────────────────
// Route: /events/:eventId/rsvp-editor
//
// Studio-side editor for the *dedicated* RSVP page reached from the
// canvas RSVP Button (public route: /invite/:slug/rsvp). Reuses the
// existing v2 RSVP section system end-to-end:
//   • Layout picker → same 6 variants used by inline RSVP sections
//   • WidgetSectionEditor → identical control surface
//   • RsvpSection → identical renderer (live preview)
//
// Data lives at event/invitation level on rsvp_pages.dedicated_rsvp_section
// (auto-seeded once from the first inline RSVP section if present;
// otherwise from sensible defaults). After the first save the dedicated
// page and any inline RSVP can diverge freely.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import WidgetSectionEditor from '../components/editor/WidgetSectionEditor';
import RsvpSection from '../components/sections/rsvp/RsvpSection';
import { buildPageLayoutCss } from '../lib/utils/rsvpPageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

// Mirrors the SECTION_VARIANTS.rsvp list inside InvitationEditor.js.
// Duplicated here because that constant isn't exported — the alternative
// would be a wider refactor we don't need for this feature.
const RSVP_VARIANTS = [
  { id: 'rsvp-card', label: 'Card' },
  { id: 'rsvp-minimal', label: 'Minimal' },
  { id: 'rsvp-soft-card', label: 'Soft Card' },
  { id: 'rsvp-luxe-card', label: 'Luxe Card' },
  { id: 'rsvp-detailed', label: 'Detailed' },
  { id: 'rsvp-immersive', label: 'Immersive' },
];

// Vertical spacing override slider. Null/undefined = inherit the
// variant's natural `py-20` (~80px). A number 0-180 overrides. The
// "Default" pill resets the override so existing pages stay untouched
// unless the designer explicitly opts in.
function SpacingSlider({ label, value, onChange, testid }) {
  const hasOverride = typeof value === 'number';
  const display = hasOverride ? `${value}px` : 'Default';
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-gray-600 dark:text-gray-400">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums w-12 text-right">{display}</span>
          {hasOverride && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-[10px] text-violet-600 hover:underline"
              data-testid={`${testid}-reset`}
            >
              Reset
            </button>
          )}
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={180}
        step={2}
        value={hasOverride ? value : 80}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-violet-600"
        data-testid={`${testid}-slider`}
      />
    </div>
  );
}

export default function RsvpPageEditor() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState(null);
  const [slug, setSlug] = useState(null);
  const [previewToken, setPreviewToken] = useState(null);
  const [seededFromInline, setSeededFromInline] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  // ─── Load (auto-seed on first call) ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${API}/api/events/${eventId}/rsvp-page/section`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setSection(res.data.section || null);
        setSlug(res.data.slug || null);
        setPreviewToken(res.data.preview_token || null);
        setSeededFromInline(!!res.data.seeded_from_inline);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Failed to load RSVP page');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, token]);

  // ─── Mutations ──────────────────────────────────────────────
  const handleUpdateData = useCallback((_sectionId, newData) => {
    setSection((s) => (s ? { ...s, data: newData } : s));
    setDirty(true);
  }, []);

  const handleVariantChange = useCallback((_sectionId, variantId) => {
    setSection((s) => (s ? { ...s, variant: variantId } : s));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!section) return;
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/events/${eventId}/rsvp-page/section`,
        { section },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDirty(false);
      toast.success('RSVP page saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [eventId, token, section]);

  const handleBack = useCallback(() => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    navigate(`/events/${eventId}/invitation-editor`);
  }, [dirty, navigate, eventId]);

  const handlePreview = useCallback(() => {
    if (!slug) return;
    const qs = previewToken ? `?preview=${encodeURIComponent(previewToken)}` : '';
    window.open(`/invite/${slug}/rsvp${qs}`, '_blank', 'noopener');
  }, [slug, previewToken]);

  // ─── Preview theme (mirrors PublicInviteRsvp) ───────────────
  const previewTheme = useMemo(() => ({
    accent: section?.data?.customStyle?.accentColor || '#1a1a1a',
    textPrimary: '#1a1a1a',
    textSecondary: 'rgba(0,0,0,0.65)',
    background: '#f7f4ef',
    bg: '#f7f4ef',
    eyebrowFont: undefined,
    bodyFont: undefined,
  }), [section]);

  // ─── Render states ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950" data-testid="rsvp-editor-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error || !section) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-950 p-6 text-center" data-testid="rsvp-editor-error">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{error || 'No RSVP page found'}</p>
        <button
          onClick={() => navigate(`/events/${eventId}/invitation-editor`)}
          className="text-sm text-violet-600 hover:underline"
          data-testid="rsvp-editor-error-back"
        >
          Back to invitation editor
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100" data-testid="rsvp-page-editor">
      {/* ─── Toolbar ────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 bg-white/85 dark:bg-gray-900/85 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            data-testid="rsvp-editor-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to invitation editor
          </button>
          <div>
            <h1 className="text-sm font-semibold tracking-tight">RSVP Page Editor</h1>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
              {seededFromInline ? 'Seeded from inline RSVP — diverges from here.' : 'Dedicated RSVP page for the canvas RSVP Button.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreview}
              disabled={!slug}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
              data-testid="rsvp-editor-preview-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Preview
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="rsvp-editor-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {dirty ? 'Save' : 'Saved'}
            </button>
          </div>
        </div>
      </header>

      {/* ─── Body: Left rail (controls) · Right (live preview) ─ */}
      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
        {/* Controls */}
        <aside className="space-y-4">
          {/* Page-layout controls — distinct from the section-level
              styling because they govern the OUTER wrapper of
              /invite/:slug/rsvp (not the surface itself). Today the
              only knob is "Edge-to-edge background" so a Background-
              width=100% surface actually touches the viewport edges on
              a phone (previously the wrapper's px-5 + max-w-md
              constrained it, which surprised designers). */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden" data-testid="rsvp-page-layout-panel">
            <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Page layout</h3>
            </div>
            <div className="p-3 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!section.data?.pageEdgeToEdge}
                  onChange={(e) => handleUpdateData(section.id, {
                    ...(section.data || {}),
                    pageEdgeToEdge: e.target.checked,
                  })}
                  className="mt-0.5 accent-violet-600"
                  data-testid="rsvp-page-edge-to-edge"
                />
                <span className="flex-1">
                  <span className="block text-[12px] font-medium text-gray-800 dark:text-gray-100">Edge-to-edge background</span>
                  <span className="block text-[11px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
                    Lets a Background width of 100% touch the viewport edges on phones. Leave off for the inset look.
                  </span>
                </span>
              </label>

              {/* Vertical breathing room — the RSVP variants ship with
                  ~80px top/bottom padding baked in (`py-20`). Override
                  here when the page feels disconnected on mobile or
                  needs more drama. Null = use variant default. */}
              <SpacingSlider
                label="Top spacing"
                value={section.data?.pageTopSpacing}
                onChange={(v) => handleUpdateData(section.id, {
                  ...(section.data || {}),
                  pageTopSpacing: v,
                })}
                testid="rsvp-page-top-spacing"
              />
              <SpacingSlider
                label="Bottom spacing"
                value={section.data?.pageBottomSpacing}
                onChange={(v) => handleUpdateData(section.id, {
                  ...(section.data || {}),
                  pageBottomSpacing: v,
                })}
                testid="rsvp-page-bottom-spacing"
              />
            </div>
          </div>

          <WidgetSectionEditor
            section={section}
            onUpdateData={handleUpdateData}
            onVariantChange={handleVariantChange}
            variants={RSVP_VARIANTS}
            eventId={eventId}
            token={token}
            onImageUpload={null}
            uploadingField={null}
          />
        </aside>

        {/* Live preview — same renderer the public page uses. iPhone
            frame keeps the designer honest about mobile constraints. */}
        <section className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Live preview</h2>
              <span className="text-[10px] text-gray-400">/invite/{slug || '...'}/rsvp</span>
            </div>
            <div className="mx-auto" style={{ width: 390, maxWidth: '100%' }}>
              {(() => {
                const edgeToEdge = !!section.data?.pageEdgeToEdge;
                const preview = buildPageLayoutCss({
                  scopeClass: 'rsvp-editor-preview-frame--scoped',
                  sectionSelector: '[data-testid="rsvp-section"]',
                  edgeToEdge,
                  topSpacing: section.data?.pageTopSpacing,
                  bottomSpacing: section.data?.pageBottomSpacing,
                });
                const innerCls = edgeToEdge
                  ? 'w-full'
                  : 'w-full max-w-md mx-auto px-5 pt-4 pb-16';
                return (
                  <div
                    className={`rounded-3xl border border-gray-200 dark:border-gray-800 overflow-hidden${preview.wrapperClass ? ' ' + preview.wrapperClass : ''}`}
                    style={{
                      height: 760,
                      backgroundColor: previewTheme.background,
                      overflowY: 'auto',
                    }}
                    data-testid="rsvp-editor-preview-frame"
                  >
                    {preview.css && <style>{preview.css}</style>}
                    <div className={innerCls}>
                      <RsvpSection
                        section={section}
                        data={section.data || {}}
                        theme={previewTheme}
                        settings={{ slug: slug || 'preview', motionPreset: 'none', disableRsvpSubmit: true }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
